import { INTEGRATION_NAMES, type OrgOnboardingStatus, type OrgPlanName } from "@tellescope/tanstack-query";
import { PLAN_FEATURES } from "./plan-features";
import { FEATURE_NAMES, type FeatureAvailability, type FeatureName } from "./types";

/**
 * Resolves the availability of every feature for a plan + onboarding status.
 *
 * - Unknown plan ⇒ every feature `{ enabled: true, status: "ready" }` (preserves
 *   pre-plan behavior where everything is visible).
 * - Base features ⇒ `{ enabled: true, status: "ready" }`.
 * - Integration-powered features ⇒ always `enabled: true` (plan-level surface);
 *   `status: "ready"` iff that integration's record value is `true`, else
 *   `"setup-required"` with `integration` set (covers skipped / unselected /
 *   mid-setup so the UI can render finish-setup states instead of hiding).
 * - Features in neither list ⇒ `{ enabled: false, status: "setup-required" }`.
 *
 * @param plan The org's narrowed plan, or `undefined`.
 * @param status The parsed onboarding status record.
 * @returns A full {@link FeatureAvailability} map keyed by every feature name.
 */
export const resolveOrgFeatures = ({
  plan,
  status,
}: {
  plan: OrgPlanName | undefined;
  status: OrgOnboardingStatus;
}): Record<FeatureName, FeatureAvailability> => {
  const compute = (feature: FeatureName): FeatureAvailability => {
    if (!plan) return { enabled: true, status: "ready" };

    const planFeatures = PLAN_FEATURES[plan];
    if (planFeatures.baseFeatures.includes(feature)) return { enabled: true, status: "ready" };

    const integration = INTEGRATION_NAMES.find((name) => planFeatures.byIntegration[name]?.includes(feature));
    if (integration) {
      return { enabled: true, status: status?.[integration] === true ? "ready" : "setup-required", integration };
    }

    return { enabled: false, status: "setup-required" };
  };

  return FEATURE_NAMES.reduce<Record<FeatureName, FeatureAvailability>>(
    (acc, feature) => {
      acc[feature] = compute(feature);
      return acc;
    },
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    {} as Record<FeatureName, FeatureAvailability>
  );
};
